/**
 * Data submission page. Offers Test Runs (validated, not stored) at REVIEW, APPROVED, and ACTIVE,
 * and Live Runs only at ACTIVE, matching the Cedar upload policies so the API never returns 403.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import Form from "@cloudscape-design/components/form"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Popover from "@cloudscape-design/components/popover"
import ProgressBar from "@cloudscape-design/components/progress-bar"
import SegmentedControl from "@cloudscape-design/components/segmented-control"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import StatusIndicator, {
  type StatusIndicatorProps,
} from "@cloudscape-design/components/status-indicator"
import Table from "@cloudscape-design/components/table"
import React, { useEffect, useMemo, useState } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import FileUploadZone from "@/components/FileUploadZone"
import { RunStatus, WorkflowStage } from "@/generated/sdk"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import { useDtaDetail, useDtaVersions } from "../../hooks/queries/useDtas"
import { useSubmissionHistory } from "../../hooks/queries/useIngestion"
import { ingestionApiService, type IngestionRun } from "../../services/ingestionApi"
import { ApiError, sdk } from "../../services/sdk"
import { validateFile, formatBytes, type FileValidationError } from "../../utils/fileValidation"
import { describeStageSubmissionBlock, mapPresignError, mapUploadError } from "./submissionErrors"

interface VendorDataSubmissionProps {
  dtaId?: string
  studyId?: string
  embedded?: boolean // Rendered inside a modal
}

type ValidationStatus = "idle" | "uploading" | "error"

const runStatusIndicatorType = (status: RunStatus): StatusIndicatorProps.Type => {
  switch (status) {
    case RunStatus.Completed:
      return "success"
    case RunStatus.Failed:
      return "error"
    case RunStatus.Running:
      return "in-progress"
    case RunStatus.PartiallyCompleted:
      return "warning"
    default:
      return "pending"
  }
}

function isActualModeAvailable(dta: {
  workflow_stage?: string
  data_stream_id?: string | null
  document_content?: { data_stream_id?: string | null } | null
}): boolean {
  const hasDataStream = !!(dta.data_stream_id || dta.document_content?.data_stream_id)
  return dta.workflow_stage === WorkflowStage.Active && hasDataStream
}

/**
 * Stages where an external caller may Test Run: Cedar `TestRunUpload` refuses INACTIVE, and DRAFT
 * is not visible to them. Internal callers get Test Run at every stage because their grants
 * decide.
 */
const VENDOR_DRY_RUN_STAGES: readonly string[] = [
  WorkflowStage.Review,
  WorkflowStage.Approved,
  WorkflowStage.Active,
]

function isDryRunModeAvailable(dta: { workflow_stage?: string }, isVendor: boolean): boolean {
  if (!isVendor) return true
  return VENDOR_DRY_RUN_STAGES.includes(dta.workflow_stage ?? "")
}

const VendorDataSubmission: React.FC<VendorDataSubmissionProps> = ({
  dtaId: propDtaId,
  studyId: propStudyId,
  embedded = false,
}) => {
  const params = useParams<{ dtaId?: string; studyId?: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isExternal: isVendor, isLoading: accessLoading } = useMyAccess()

  const dtaId = propDtaId || params.dtaId
  const studyId = propStudyId || params.studyId
  const targetVersion = searchParams.get("version")

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const [currentStage, setCurrentStage] = useState<string>("")
  const [localValidationErrors, setLocalValidationErrors] = useState<FileValidationError[]>([])
  const [submissionMode, setSubmissionMode] = useState<"TEST" | "ACTUAL">("TEST")

  // Load the pinned version if any; a 403 (no longer visible to this vendor) falls back to latest.
  const {
    data: dta = null,
    isLoading: dtaLoading,
    error: dtaError,
  } = useDtaDetail(dtaId, targetVersion || undefined)
  const forbiddenPinnedVersion =
    dtaError instanceof ApiError && dtaError.status === 403 && !!targetVersion
  const resolvedStudyId = studyId ?? dta?.study_id
  // Which modes a vendor may use depends on their experience, so wait for it too.
  const loading = dtaLoading || accessLoading

  // Use every version's spec id so earlier versions' submissions stay in history after an amendment.
  const { data: versions = [] } = useDtaVersions(dtaId)
  const specIds = useMemo(
    () => [dta?.ingestion_spec_id, ...versions.map((version) => version.ingestion_spec_id)],
    [dta?.ingestion_spec_id, versions],
  )
  const loadError = !dtaId
    ? "DTA ID is required"
    : dtaError && !forbiddenPinnedVersion
      ? "Failed to load DTA details. Please try again."
      : null

  // Drop a forbidden ?version= so the hook reloads the latest visible one. This works on both
  // submit routes.
  useEffect(() => {
    if (!forbiddenPinnedVersion) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("version")
        return next
      },
      { replace: true },
    )
  }, [forbiddenPinnedVersion, setSearchParams])

  const [historyView, setHistoryView] = useState<"TEST" | "ACTUAL">("TEST")
  // True between a successful upload and the new run appearing in history.
  const [processingUpload, setProcessingUpload] = useState<boolean>(false)

  const {
    data: submissionHistory,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useSubmissionHistory(resolvedStudyId, specIds)

  // Default to ACTUAL when available, else TEST.
  useEffect(() => {
    if (dta) setSubmissionMode(isActualModeAvailable(dta) ? "ACTUAL" : "TEST")
  }, [dta])

  useEffect(() => {
    if (processingUpload && submissionHistory.length > 0) setProcessingUpload(false)
  }, [processingUpload, submissionHistory])

  const handleViewRunDetails = (runId: string) => {
    if (resolvedStudyId) {
      navigate(`/studies/${resolvedStudyId}/runs/${runId}`)
    }
  }

  const handleModeChange = (mode: "TEST" | "ACTUAL") => {
    // Keep the file and its validation across mode switches, since both modes share file rules.
    // Clear only the submission error.
    setSubmissionMode(mode)
    setError(null)
  }

  const handleFileSelect = (file: File) => {
    setSelectedFile(file)
    setLocalValidationErrors([])
    setError(null)

    if (dta) {
      const allowedFormats = getAcceptedFormats()
      const requireZip = getRequireZip()

      const errors = validateFile(file, {
        maxSizeMB: 500,
        allowedFormats,
        requireZip,
      })

      if (errors.length > 0) {
        setLocalValidationErrors(errors)
      }
    }
  }

  const handleSubmit = async () => {
    if (!selectedFile || !dta || !dtaId) {
      setError("Missing required information for submission")
      return
    }

    if (localValidationErrors.length > 0) {
      setError("Please fix validation errors before submitting")
      return
    }

    setValidationStatus("uploading")
    setError(null)
    setUploadProgress(0)

    const isDryRun = submissionMode === "TEST"

    let uploadUrl: string
    try {
      setCurrentStage("Requesting upload URL...")

      const response = await sdk.uploadToDta({
        dtaId,
        uploadToDtaRequest: {
          files: [{ fileName: selectedFile.name }],
          dryRun: isDryRun,
          // Use the loaded version, not the possibly stale URL version.
          dtaVersion: dta.version,
        },
      })

      if (!response.files || response.files.length === 0) {
        throw new Error("Empty response from server")
      }

      const url = response.files[0].uploadUrl
      if (!url) {
        throw new Error("Empty response from server")
      }
      uploadUrl = url
    } catch (err) {
      console.error("Presign error:", err)
      const mapped = mapPresignError(err, dta.workflow_stage)
      setValidationStatus("error")
      setError(mapped.userMessage)
      return
    }

    try {
      setCurrentStage(
        isDryRun
          ? "Uploading file for Test Run validation..."
          : "Uploading file for Live Run submission...",
      )

      await ingestionApiService.uploadFileToS3(uploadUrl, selectedFile, (percent) => {
        setUploadProgress(percent)
      })

      setValidationStatus("idle")
      setCurrentStage("")
      setUploadProgress(0)
      setProcessingUpload(true)
      void refetchHistory()

      setSelectedFile(null)
      setHistoryView(isDryRun ? "TEST" : "ACTUAL")
    } catch (err) {
      console.error("Upload error:", err)
      const mapped = mapUploadError(err)
      setValidationStatus("error")
      setError(mapped.userMessage)
    }
  }

  const getAcceptedFormats = (): string[] => {
    // Accept any format for now
    return []
  }

  const getRequireZip = (): boolean => {
    return (dta?.document_content?.require_zip as boolean) || false
  }

  const isSubmitDisabled = (): boolean => {
    return (
      !selectedFile ||
      localValidationErrors.length > 0 ||
      validationStatus === "uploading" ||
      loading ||
      // No submission mode can succeed at this stage.
      (!!dta && !isActualModeAvailable(dta) && !isDryRunModeAvailable(dta, isVendor))
    )
  }

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl" color="text-status-inactive">
        <SpaceBetween size="s" alignItems="center">
          <Spinner size="large" />
          <span>Loading DTA details...</span>
        </SpaceBetween>
      </Box>
    )
  }

  if (loadError && !dta) {
    return (
      <Box padding="l">
        <Alert
          type="error"
          header="Error Loading DTA"
          action={<Button onClick={() => navigate(-1)}>Go Back</Button>}
        >
          {loadError}
        </Alert>
      </Box>
    )
  }

  if (!dta) {
    return (
      <Box padding="l" textAlign="center" color="text-status-inactive">
        DTA not found
      </Box>
    )
  }

  // Only ACTIVE DTAs with a data stream allow actual submissions.
  const actualModeUnavailable = !isActualModeAvailable(dta)
  const dryRunUnavailable = !isDryRunModeAvailable(dta, isVendor)
  // Explain a stage block before the user picks a file, not via a 403 after uploading.
  const stageBlockExplanation = describeStageSubmissionBlock(dta.workflow_stage ?? "")
  const noSubmissionsAccepted = actualModeUnavailable && dryRunUnavailable

  const submitLabel =
    validationStatus === "uploading"
      ? "Uploading..."
      : validationStatus === "error"
        ? "Retry Submission"
        : submissionMode === "ACTUAL"
          ? "Submit Live Run"
          : "Submit Test Run"

  const dryRunCount = submissionHistory.filter((r) => r.dryRun).length
  const actualRunCount = submissionHistory.filter((r) => !r.dryRun).length
  const visibleRuns = submissionHistory.filter((run) =>
    historyView === "TEST" ? run.dryRun : !run.dryRun,
  )

  const modeControls = (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <Box variant="span">Submission Mode:</Box>
      <SegmentedControl
        selectedId={submissionMode}
        onChange={({ detail }) => handleModeChange(detail.selectedId as "TEST" | "ACTUAL")}
        label="Submission mode"
        options={[
          {
            id: "TEST",
            text: "Test Run",
            disabled: validationStatus === "uploading" || dryRunUnavailable,
          },
          {
            id: "ACTUAL",
            text: "Live Run",
            disabled: validationStatus === "uploading" || actualModeUnavailable,
          },
        ]}
      />
      {actualModeUnavailable && (
        <Popover
          position="top"
          size="medium"
          triggerType="custom"
          header="Live Run unavailable"
          content={
            dryRunUnavailable
              ? "Live Run submissions are only available when the DTA is ACTIVE. Test Run is also unavailable at this stage, so this DTA accepts no submissions right now."
              : "Live Run submissions are only available when the DTA is ACTIVE. In the meantime, use Test Run to validate your file format - a Test Run checks your file against the DTA's rules without storing any data."
          }
        >
          <Button
            variant="inline-icon"
            iconName="status-info"
            ariaLabel="Why is Live Run unavailable?"
          />
        </Popover>
      )}
    </SpaceBetween>
  )

  const content = (
    <SpaceBetween size="l">
      <Container
        header={
          <Header variant="h2" actions={modeControls}>
            {`DTA Status: ${dta.workflow_stage}`}
          </Header>
        }
      >
        <SpaceBetween size="s">
          {stageBlockExplanation ? (
            // Blocked stages get one Alert with the explanation and, when Test Run is
            // available, the mode definitions, so no second message stacks below.
            <Alert type="info">
              {noSubmissionsAccepted
                ? `${stageBlockExplanation} Contact your study administrator if this is unexpected.`
                : `${stageBlockExplanation} Test Run validates your file format — nothing is stored.`}
            </Alert>
          ) : (
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Icon name="status-info" variant="link" />
              <Box fontSize="body-s" color="text-body-secondary">
                Test Run: validates your file format — nothing is stored. Live Run: your data is
                stored permanently.
              </Box>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>

      {/* Cloudscape File-upload pattern: Submit sits in the Form footer and errors in the
          Form's error slot, right above it. */}
      <Container header={<Header variant="h2">Select File</Header>}>
        <form
          id="vendor-submission-form"
          onSubmit={(event) => {
            event.preventDefault()
            void handleSubmit()
          }}
        >
          <Form
            errorText={error}
            errorIconAriaLabel="Error"
            actions={
              <Button
                variant="primary"
                formAction="submit"
                form="vendor-submission-form"
                disabled={isSubmitDisabled()}
                loading={validationStatus === "uploading"}
              >
                {submitLabel}
              </Button>
            }
          >
            <SpaceBetween size="m">
              <FormField label="Data file" stretch>
                <FileUploadZone
                  onFileSelect={handleFileSelect}
                  acceptedFormats={getAcceptedFormats()}
                  maxSizeMB={500}
                  disabled={validationStatus === "uploading"}
                  requireZip={getRequireZip()}
                />
              </FormField>

              {selectedFile && localValidationErrors.length === 0 && (
                <SpaceBetween direction="horizontal" size="s" alignItems="center">
                  <Icon name="file" size="big" />
                  <SpaceBetween size="xxxs">
                    <Box variant="strong">{selectedFile.name}</Box>
                    <Box fontSize="body-s" color="text-body-secondary">
                      {formatBytes(selectedFile.size)}
                    </Box>
                  </SpaceBetween>
                  <Button
                    variant="inline-icon"
                    iconName="close"
                    ariaLabel="Close"
                    onClick={() => {
                      setSelectedFile(null)
                      setLocalValidationErrors([])
                    }}
                  />
                </SpaceBetween>
              )}

              {localValidationErrors.length > 0 && (
                <Alert type="error" header="Validation Errors">
                  <SpaceBetween size="s">
                    {localValidationErrors.map((err, idx) => (
                      <div key={idx}>
                        <Box variant="strong">{err.message}</Box>
                        <Box color="text-body-secondary">{err.suggestedFix}</Box>
                      </div>
                    ))}
                  </SpaceBetween>
                </Alert>
              )}

              {validationStatus === "uploading" && (
                <ProgressBar value={uploadProgress} label={currentStage} />
              )}

              {processingUpload && (
                <StatusIndicator type="in-progress">
                  File uploaded. Waiting for processing to begin...
                </StatusIndicator>
              )}
            </SpaceBetween>
          </Form>
        </form>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SegmentedControl
                selectedId={historyView}
                onChange={({ detail }) => setHistoryView(detail.selectedId as "TEST" | "ACTUAL")}
                label="Submission history view"
                options={[
                  { id: "TEST", text: `Test Runs (${dryRunCount})` },
                  { id: "ACTUAL", text: `Live Runs (${actualRunCount})` },
                ]}
              />
            }
          >
            Submission History
          </Header>
        }
      >
        <Table<IngestionRun>
          variant="embedded"
          loading={historyLoading}
          loadingText="Loading submission history..."
          items={visibleRuns}
          trackBy="runId"
          columnDefinitions={[
            {
              id: "runId",
              header: "Run ID",
              cell: (run) => `${run.runId.substring(0, 8)}...`,
            },
            {
              id: "fileName",
              header: "File Name",
              cell: (run) => run.fileName || "Unknown",
            },
            {
              id: "status",
              header: "Status",
              cell: (run) => (
                <StatusIndicator type={runStatusIndicatorType(run.status)}>
                  {run.status}
                </StatusIndicator>
              ),
            },
            {
              id: "created",
              header: "Created",
              cell: (run) => new Date(run.createdAt).toLocaleString(),
            },
            {
              id: "lastModified",
              header: "Last Modified",
              cell: (run) =>
                run.lastModifiedAt ? new Date(run.lastModifiedAt).toLocaleString() : "-",
            },
            {
              id: "actions",
              header: "Actions",
              cell: (run) => (
                <Button variant="inline-link" onClick={() => handleViewRunDetails(run.runId)}>
                  View Details
                </Button>
              ),
            },
          ]}
          empty={
            <Box textAlign="center" color="inherit">
              No {historyView === "TEST" ? "Test Runs" : "Live Runs"} yet
            </Box>
          }
        />
      </Container>
    </SpaceBetween>
  )

  if (embedded) {
    return content
  }

  return (
    <ContentLayout
      header={
        <SpaceBetween size="m">
          <Box padding={{ top: "s" }}>
            <Button iconName="arrow-left" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Box>
          <Header
            variant="h1"
            description={
              <>
                Study:{" "}
                <Box variant="strong" display="inline">
                  {resolvedStudyId || "Loading..."}
                </Box>{" "}
                • Domain:{" "}
                <Box variant="strong" display="inline">
                  {dta?.domains?.[0] || "—"}
                </Box>{" "}
                • Version:{" "}
                <Box variant="strong" display="inline">
                  v{dta?.version || "—"}
                </Box>
              </>
            }
          >
            Submit Data File
          </Header>
        </SpaceBetween>
      }
    >
      {content}
    </ContentLayout>
  )
}

export default VendorDataSubmission
